import { Head } from 'vite-react-ssg';
import { useParams } from 'react-router';

import { useActiveLocale } from '../i18n/LocaleContext';
import { liveLocale, DEFAULT_LOCALE } from '../i18n/locales';
import { pageMeta, SITE_URL, type PageMetaKey } from './meta';
import { isPublicSite } from './site-url';

type Descriptor = Record<string, unknown>;

/**
 * Per-page SEO head, library-mode replacement for the design app's framework-mode
 * `meta` exports (DIT-264 / S8 + S7). `pageMeta()` is the same locale-aware
 * generator ported 1:1 from done-it-design; it produces RR `MetaDescriptor`s
 * (title / description / canonical / hreflang / og / twitter) which we render
 * into the document head via vite-react-ssg's <Head>. <Head> works both at
 * prerender (real HTML for crawlers — S7) and on the client (SPA navigation).
 *
 * The locale comes from the active `:lang` (LocaleContext, set by LocaleLayout)
 * with `params.lang` as a fallback; the slug (industry / blog detail) from
 * `params.slug`. So a screen only needs `<Seo pageKey="home" />` — wired at the
 * route level by `seoPage()` in the router.
 */
export function Seo({ pageKey }: { pageKey: PageMetaKey }) {
  const active = useActiveLocale();
  const params = useParams();
  const lang = active?.code ?? params.lang;
  const items = pageMeta(pageKey, lang, params.slug) as Descriptor[];
  // BCP-47 value for <html lang> (e.g. "fr-BE", not "fr-be"); falls back to the
  // default locale for unknown segments. Emitted via Head so each prerendered
  // page carries its own lang (the static index.html shell is locale-agnostic).
  const htmlLang = (liveLocale(lang) ?? liveLocale(DEFAULT_LOCALE)!).htmlLang;

  return (
    <Head>
      <html lang={htmlLang} />
      {/* Keep the un-cutover staging domain (machine-translated, un-reviewed copy)
          out of the index until VITE_SITE_URL flips to the real domain — ported
          from the design app's root.tsx (one lever flips domain + indexability). */}
      {!isPublicSite(SITE_URL) && <meta name="robots" content="noindex,nofollow" />}
      {items.map((d, i) => {
        if ('title' in d) return <title key={i}>{String(d.title)}</title>;
        if (d.tagName === 'link') {
          return (
            <link
              key={i}
              rel={d.rel as string}
              hrefLang={d.hreflang as string | undefined}
              href={d.href as string}
            />
          );
        }
        if ('property' in d) {
          return <meta key={i} property={d.property as string} content={d.content as string} />;
        }
        if ('name' in d) {
          return <meta key={i} name={d.name as string} content={d.content as string} />;
        }
        return null;
      })}
    </Head>
  );
}
